import { Camera, ChevronDown, Image as ImageIcon, Images, SwitchCamera, Upload, X } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { AnimatedImage } from './animated-image';
import { useArmedDelete } from './block-actions';
import { BlockCard, BlockPanelCard } from './block-card';
import { IssueBar, type IssueContext, useIssueContext , type BlockIssue } from './block-issues';
import { BlockNameField } from './block-name-field';
import { LibraryModal } from './library-modal';
import { MediaOverlayControls } from './media-overlay-controls';
import { MediaPlaceholder } from './media-placeholder';
import { RichTextEditor } from './rich-text-editor';
import { useIsMobile } from './task-content';
import { DisplayBankRow, DisplayScenarioBar, useDisplayPhoneTest, useDisplayVersions } from './use-display-versions';
import { PreviewHtml } from './variable-chip-preview';

import type { SharedEditorToolbar } from './use-shared-editor-toolbar';
import type { BlockUpdateOptions } from '@/lib/block-update';
import type { Block } from '@/types/experience';
import type { ImageAspectRatio, ImageBlockData } from '@agavi/blocks';
import type { Editor } from '@tiptap/core';

import Toast from '@/components/toast';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { ChoiceTile } from '@/components/ui/choice-tile';
import { TextField } from '@/components/ui/text-field';
import { useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { assetsApi, peekAsset, type AssetSummary } from '@/lib/assets-api';
import { isNameTaken } from '@/lib/block-naming';
import { IMAGE_ACCEPT, isAllowedImageMimeType, maxUploadBytesFor } from '@/lib/image-uploads';

interface ImageBlockProps {
  block: Block;
  experienceId: string;
  taskId?: string;
  blocks?: Block[];
  isPreview: boolean;
  onUpdate: (data: Record<string, unknown>, opts?: BlockUpdateOptions) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  onRename?: (name: string) => void;
  /**
   * A toolbar the HOST already shows, for the caption to use instead of drawing its own.
   *
   * The caption is this block's one rich field, so on the canvas it renders its own bar and that
   * is right. Inside a host that already has one -- the exit editor, where several rows share a
   * single pinned bar -- a second full grid per image is the "richtext buttons everywhere"
   * problem. Absent means the canvas behaviour, unchanged.
   */
  sharedToolbar?: SharedEditorToolbar;
  /**
   * The LITE shape, for a host that is already a card.
   *
   * Drops the three pieces of furniture that only make sense on the canvas -- the title header
   * with the block's name and its close `X`, the scenario bank, and the editor's generous page
   * padding -- and leaves the frame to the host. Everything that makes this an image block
   * stays, alt text and motion description included: those are the reason the exit editor
   * renders the real component instead of hand-rolling a picker.
   */
  condensed?: boolean;
}


const ASPECT_RATIOS: { value: ImageAspectRatio; labelKey: string }[] = [
  { value: 'original',  labelKey: 'blocks.image.aspectOriginal' },
  { value: 'landscape', labelKey: 'blocks.image.aspectLandscape' },
  { value: 'square',    labelKey: 'blocks.image.aspectSquare' },
  { value: 'portrait',  labelKey: 'blocks.image.aspectPortrait' },
];

const ASPECT_BOX_CLASSES: Record<Exclude<ImageAspectRatio, 'original'>, string> = {
  landscape: 'aspect-video',
  square: 'aspect-square',
  portrait: 'aspect-[9/16]',
};

// Fixed height everywhere a placed image is previewed (editor + collapsed card) — switching
// aspect ratio only changes the crop/width, never the height, so it never reflows surrounding UI.
const PREVIEW_HEIGHT_CLASS = 'h-64';
/**
 * The same picture, smaller, for a condensed editor.
 *
 * 256px of preview is right beside the canvas, where the editor owns a whole panel. It is not
 * right in a dialog row that is one of up to five: it was 43% of a 592px row on its own, and
 * the picture is there to confirm WHICH image this is, which half the size does just as well.
 */
const CONDENSED_PREVIEW_HEIGHT_CLASS = 'h-32';

// A single <img> with an explicit height and (for the fixed ratios) a CSS aspect-ratio class
// renders at exactly that box — object-cover then crops the source to fill it. 'original' skips
// the aspect-ratio class entirely so the browser's default replaced-element sizing preserves the
// image's own native proportions (width auto-computed from height), with object-contain as a
// defensive fallback against any ancestor width clamping.
function previewImgClasses(
  ratio: ImageAspectRatio,
  extra = '',
  heightClass = PREVIEW_HEIGHT_CLASS,
  /**
   * CAP the height rather than fix it — for a reader, not an author.
   *
   * At `original` the image is drawn `object-contain` inside whatever box it is given, so a
   * FIXED height letterboxes anything wider than that box: the element stays 256px tall and
   * the picture is painted in the middle of it with dead space above and below. On an exit
   * that reads as a gap between the text and the image, which is what it was reported as, and
   * nothing on screen explains it because the space belongs to the image's own box.
   *
   * The fixed height is an AUTHORING device — the editor's note above says it is there so
   * switching aspect ratio never reflows the fields underneath. A student has no such control
   * to protect, so their copy takes the picture's own height and only refuses to exceed it.
   *
   * Only at `original`. The fixed ratios set their box with an aspect class and fill it with
   * `object-cover`, so they never letterbox, and a `max-h` there would leave the box with no
   * height to compute a width from.
   */
  fit = false,
): string {
  if (ratio === 'original') {
    const h = fit ? heightClass.replace(/^h-/, 'max-h-') : heightClass;
    return `${h} w-auto max-w-full object-contain ${extra}`;
  }
  return `${heightClass} ${ASPECT_BOX_CLASSES[ratio]} object-cover ${extra}`;
}

const ASPECT_SWATCH_DIMS: Record<Exclude<ImageAspectRatio, 'original'>, string> = {
  landscape: 'h-3 w-5',
  square: 'h-4 w-4',
  portrait: 'h-5 w-3',
};

// Small rectangle glyphs so the shape of each ratio is legible without reading the label.
function AspectSwatch({ value }: { value: ImageAspectRatio }) {
  if (value === 'original') {
    return <span aria-hidden='true' className='inline-block h-4 w-4 shrink-0 rounded-[2px] border border-dashed border-ws-muted' />;
  }
  return <span aria-hidden='true' className={`inline-block shrink-0 rounded-[2px] bg-ws-muted ${ASPECT_SWATCH_DIMS[value]}`} />;
}

/**
 * What VARIES between scenarios, and what is shared — §3.2's split.
 *
 * The picture and the words that describe it are the content, so they travel with the variant.
 * `aspectRatio` is how an image is FRAMED rather than which image it is, so it stays on the
 * block: a group that changed shape as it stepped would be the layout moving, not the scenario.
 */
// `motionDescription` travels with the image for the same reason `altText` does: a variant that
// swaps in a different animation describes different motion. `autoplay` does NOT — like
// `aspectRatio` it is how the image is PRESENTED rather than which image it is.
const IMAGE_VERSION_KEYS = ['assetId', 'altText', 'caption', 'motionDescription'] as const;

function toData(raw: Record<string, unknown>): ImageBlockData {
  return {
    assetId: (raw.assetId as string | null | undefined) ?? null,
    animated: raw.animated as boolean | undefined,
    // ORIGINAL on insert. A teacher who uploads a portrait photo and sees it cropped to landscape
    // has to discover the setting before the block shows what they actually chose; starting from
    // the image's own shape means the default is never wrong, only sometimes not what they want.
    aspectRatio: (raw.aspectRatio as ImageAspectRatio) ?? 'original',
    altText: raw.altText as string | undefined,
    caption: raw.caption as string | undefined,
    motionDescription: raw.motionDescription as string | undefined,
    // A teacher who placed an animation meant it to animate; the control is right there to say
    // otherwise. Reduced motion overrides this regardless of what is stored.
    autoplay: (raw.autoplay as boolean | undefined) ?? true,
  };
}

/** The alt-text box, as `focusIssueField` addresses it. One constant so the issue and the field
 *  cannot drift — the same reason `BlockIssue.fields` exists at all. */
/**
 * The alt-text box, addressed by BOTH an issue and an undo.
 *
 * Its value is the DATA KEY, not a kebab-cased label. `data-field-id` started as the issues system's
 * vocabulary, and undo now uses the same attribute to say which field it took back — so a control
 * whose id does not match its key is reachable by one and invisible to the other. This one was
 * `'alt-text'`; `TRANSCRIPT_FIELD` next door was already `'transcript'`, which is the convention.
 */
const ALT_TEXT_FIELD = 'altText';

/** Same convention as ALT_TEXT_FIELD above: the value IS the data key, so the issues system and
 *  undo's field reveal both reach it. */
const MOTION_DESCRIPTION_FIELD = 'motionDescription';

// Missing alt-text on a placed image is a real accessibility error, not a warning — without it a
// screen-reader user has no way to know what the image even is (same standing rule MCQ/Ordering's
// own image-option alt-text check already applies).
/**
 * ONLY WHAT THIS BLOCK AUTHORED — the owner's rule, and the thing that makes this check PURE.
 *
 * It used to read `data.altText ?? cachedAsset?.description`, and flagged only when BOTH were
 * empty. Two problems with that. Accessibility: an asset's description is a library caption,
 * written once for a file that may appear in three different questions, and alt text is a property
 * of the USE — an image whose own field is empty is not described, whatever the library says about
 * the file. And reachability: the fallback is the only thing that dragged the network into an
 * otherwise pure check, so nothing outside this component could run it without resolving assets
 * first, which the experience board cannot do without racing.
 *
 * `animated` likewise comes off the block now. Only the server knows whether a `.webp` moves, so it
 * is recorded when the asset is PICKED and read from the data here.
 */
/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `imageIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function imageBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return imageIssues(toData(block.data) as unknown as ImageBlockData, ctx);
}

export function imageIssues(d: ImageBlockData, ctx: IssueContext): BlockIssue[] {
  const { t } = ctx;
  const effectiveAlt = d.altText ?? '';
  const effectiveMotion = d.motionDescription ?? '';
  const isAnimated = d.animated === true;
  if (!d.assetId) return [];
  const issues: BlockIssue[] = [];
  // `incompletion`, not `defect`: nothing contradicts anything — the alt text simply has not been
  // written yet, and firing the instant an image is placed interrupts the teacher mid-task. The two
  // axes are independent: this stays `severity: 'error'` and still gates publication. `kind` only
  // decides WHEN it surfaces.
  if (!effectiveAlt.trim()) {
    issues.push({ key: 'alt-text-missing', fields: [ALT_TEXT_FIELD], severity: 'error', kind: 'incompletion', category: t('blocks.image.altText'), detail: t('blocks.image.missingAltText') });
  }
  // Same severity and kind as alt text, and as Sound's own missing-transcript — all three are one
  // rule (WCAG 1.2.1: content a student cannot perceive needs a text equivalent), so a teacher who
  // has met it once meets it the same way everywhere. Only asked for when the asset actually
  // animates; a still has no motion to describe and the field is not shown.
  if (isAnimated && !effectiveMotion.trim()) {
    issues.push({ key: 'motion-description-missing', fields: [MOTION_DESCRIPTION_FIELD], severity: 'error', kind: 'incompletion', category: t('blocks.image.motionDescription'), detail: t('blocks.image.missingMotionDescription') });
  }
  return issues;
}

// Same shape as every migrated input block's own top-issue bar (see e.g. ordering-block.tsx's
// orderingCollapsedIssueBar / mcq-block.tsx's mcqBarNode) — middle-aligned icon+text, text-sm, so
// this display block's own warning reads identically to theirs instead of the denser, top-aligned
// text-xs treatment `IssueRow` uses for a stacked multi-issue editor panel (a shape this block,
// with only ever one possible issue, never needs).
export default function ImageBlock({ block, experienceId, taskId = '', blocks = [], isPreview, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver, isMain = false, embedded, onClose, mobileIsEditing, onRename, sharedToolbar, condensed }: ImageBlockProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  /* Held so the caption can hand itself to a host's toolbar on focus — see `sharedToolbar`. */
  const captionEditorRef = useRef<Editor | null>(null);
  const issueCtx = useIssueContext(block.id, blocks);
  const stored = toData(block.data);
  const missingAltTextWarningId = useId();
  const missingMotionWarningId = useId();
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !stored.assetId));
  const taskPreviewRoll = useTaskPreviewRoll();
  /* The bank and its three-way "which variant" rule, shared with Text, Sound and DataViz. */
  const {
    bank, leadsGroup, shown, shownVersionId, setTestVersion,
    editorVersionId, setEditorVersion, writeFields, addVariant, removeVariant, renameVariant, removeWarning,
  } = useDisplayVersions(block, IMAGE_VERSION_KEYS, onUpdate, {
    isPreview, isEditing, taskPreviewRoll, blocks,
    nameFor: index => t('blocks.versions.scenarioDefault', { index }),
    warnFor: (names: string[]) => t('blocks.scenario.removeWarning', { names: names.join(', ') }),
    base: stored as unknown as Record<string, unknown>,
  });

  /*
   * ABOVE EVERY EARLY RETURN. `isPreview` and friends bail out further down, so declaring these
   * beside `usePortal` meant the hooks ran only on some renders — which is a crash the moment a
   * block flips into Preview, not only a lint complaint.
   */
  const [phoneMode, setPhoneMode] = useState<'edit' | 'test'>('edit');
  const usePortal = isMobile && !embedded;
  /* Edit/Test on the phone, the surface Text already had. Hidden rather than unmounted so the
     editor keeps its state across a tab flip. See `useDisplayPhoneTest`. */
  const phoneTest = useDisplayPhoneTest({
    block, bank, shownVersionId, setTestVersion, phoneMode, setPhoneMode, enabled: usePortal,
  });
  /** Everything below reads the VARIANT IN FORCE, never the block's own vestigial copy. */
  const data = shown as unknown as ImageBlockData;
  useLayoutEffect(() => {
    if (mobileIsEditing === undefined) return;
    setIsEditing(mobileIsEditing);
    // Mirrors handleDone's cleanup — the mobile panel can close via the parent's own
    // mechanism (tap-outside, etc.) without ever calling handleDone directly.
    if (!mobileIsEditing) setJustUploaded(false);
  }, [mobileIsEditing]);

  // Refocuses the collapsed card once the editor closes — mirrors the collapsedContentRef pattern
  // already used by mcq-block.tsx/dropdown-block.tsx/slider-block.tsx, which this display block
  // (hand-rolled, not built on the shared input-block frame) didn't have yet.
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);
  const [isDragging, setIsDragging] = useState(false);
  const [aspectOpen, setAspectOpen] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [cameraFacing, setCameraFacing] = useState<'environment' | 'user'>('environment');
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const aspectRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  /**
   * Seeded from the cache, so an asset already in memory is on screen in the FIRST frame.
   *
   * The effect below resolves the asset asynchronously, which meant every mount of this block
   * started empty — including the second, third and tenth time a teacher opened a task they had
   * just been looking at. The data was already in hand; the block simply never asked for it
   * before rendering, so it drew a gap and filled it a tick later. Stepping between tasks made
   * that visible as content jumping around.
   *
   * A `useState` INITIALIZER, not an effect: it has to run before the first paint, and anything
   * later is a frame too late by definition. The effect still runs, finds the id already matched
   * and returns, so a cache hit now costs one comparison instead of a round trip.
   */
  const [cachedAsset, setCachedAsset] = useState<AssetSummary | null>(
    () => (data.assetId ? peekAsset(data.assetId) ?? null : null),
  );
  /** The fetch came back empty-handed. Distinct from "not yet", which looks identical in state and
   * means the opposite to whoever is waiting. */
  const [assetFailed, setAssetFailed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [renameValue, setRenameValue] = useState('');
  // Only offer the rename field right after this session places a brand-new asset (upload/capture) —
  // not for one reused from the Library, and not once the editor has been closed and reopened.
  const [justUploaded, setJustUploaded] = useState(false);

  // Lazily resolve the placed asset (fresh signed URLs + inherited description/caption)
  // whenever the block only has an assetId persisted (e.g. on first load, or after a pick).
  useEffect(() => {
    if (!data.assetId) { setCachedAsset(null); setAssetFailed(false); return; }
    if (cachedAsset?.id === data.assetId) return;
    let cancelled = false;
    setAssetFailed(false);
    assetsApi.get(data.assetId)
      .then((asset) => {
        if (cancelled) return;
        setCachedAsset(asset);
        /* HEAL THE BOOKKEEPING, for a block authored before `animated` was recorded on it. The
           motion check reads the block now, so a block that never learned the answer would stay
           silent about a missing description for ever. Written only when it actually differs, so
           this is not an edit the teacher made and not a step an undo should land on — the same
           contract every other self-heal on mount keeps. */
        /* `normalization: true` is what keeps this OFF THE UNDO STACK. It travels the same write
           path a teacher's edit does, so without the flag a block that merely learned whether its
           asset moves leaves an undo step nobody made — the exact fault A5 was written for, and
           one I shipped here before checking how Numerical's own self-heal avoids it. */
        if (!!asset.animated !== (data.animated === true)) {
          set({ animated: !!asset.animated }, { normalization: true });
        }
      })
      // The toast is for the editor. Preview has no toast of its own and, before the placeholder
      // below, no block either — so a failure there was entirely silent.
      .catch(() => { if (!cancelled) { setAssetFailed(true); setToastMsg(t('blocks.image.loadError')); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.assetId]);

  /** Re-fetch, for the placeholder's Try again. The same shape Sound has had all along: a signed URL
   * that expired mid-session cannot be recovered by requesting it again, only by asking for a new
   * one, so this goes back to the asset rather than reloading an image. */
  function refreshCachedAsset() {
    if (!data.assetId) return;
    setAssetFailed(false);
    assetsApi.get(data.assetId)
      .then(setCachedAsset)
      .catch(() => { setAssetFailed(true); setToastMsg(t('blocks.image.loadError')); });
  }

  useEffect(() => {
    setRenameValue(cachedAsset?.filename ?? '');
  }, [cachedAsset?.id, cachedAsset?.filename]);

  async function saveRename() {
    if (!cachedAsset) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === cachedAsset.filename) { setRenameValue(cachedAsset.filename); return; }
    try {
      const updated = await assetsApi.update(cachedAsset.id, { filename: trimmed });
      setCachedAsset(updated);
    } catch {
      setToastMsg(t('blocks.image.renameError'));
      setRenameValue(cachedAsset.filename);
    }
  }

  // Upload/Camera always produce a brand-new asset and repoint just this block —
  // never mutate a shared asset's content. Swapping a shared asset's content is a
  // deliberate, separate action that only lives inside the Library modal.
  async function handleNewFile(file: File) {
    if (!isAllowedImageMimeType(file.type)) {
      setToastMsg(t('blocks.image.unsupportedFileType'));
      return;
    }
    const maxBytes = maxUploadBytesFor(file.type);
    if (file.size > maxBytes) {
      setToastMsg(t('blocks.image.fileTooLarge', { maxMB: Math.round(maxBytes / (1024 * 1024)) }));
      return;
    }
    setUploading(true);
    try {
      const uploaded = await assetsApi.upload(file);
      setCachedAsset(uploaded);
      set({ assetId: uploaded.id, animated: !!uploaded.animated, altText: undefined, caption: undefined, motionDescription: undefined });
      setJustUploaded(true);
      if (uploaded.deduped) setToastMsg(t('blocks.image.library.deduped'));
      setCameraActive(false);
    } catch (err) {
      setToastMsg(err instanceof Error ? err.message : t('blocks.image.uploadError'));
    } finally {
      setUploading(false);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleNewFile(file);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void handleNewFile(file);
  }

  function handleLibrarySelect(asset: AssetSummary, chosen: { altText?: string; caption?: string; motionDescription?: string }) {
    setCachedAsset(asset);
    // `motionDescription` is carried whatever the asset is. Written unconditionally rather than
    // only for an animated one, so swapping a still in over an animation CLEARS the stale
    // description instead of leaving it describing a picture that is no longer there.
    set({ assetId: asset.id, animated: !!asset.animated, altText: chosen.altText, caption: chosen.caption, motionDescription: chosen.motionDescription });
    setJustUploaded(false);
    setShowLibraryModal(false);
  }

  function handleClear() {
    setCachedAsset(null);
    set({ assetId: null, animated: undefined, altText: undefined, caption: undefined, motionDescription: undefined });
    setJustUploaded(false);
  }

  /* Through the version being edited when there is a bank — and merged onto the WHOLE stored
     object either way, because `onUpdate` replaces `block.data` and a write naming only what
     changed would delete the bank on the first edit. */
  function set(patch: Partial<ImageBlockData>, opts?: BlockUpdateOptions) {
    writeFields(patch as Record<string, unknown>, opts);
  }

  function handleDone() {
    setJustUploaded(false);
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  useEffect(() => {
    if (!aspectOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (aspectRef.current && !aspectRef.current.contains(e.target as Node)) {
        setAspectOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [aspectOpen]);

  function stopCamera() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  async function startCamera() {
    setCameraError('');
    stopCamera();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: cameraFacing } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('blocks.image.cameraError');
      setCameraError(msg);
    }
  }

  function flipCamera() {
    setCameraFacing((f) => (f === 'environment' ? 'user' : 'environment'));
  }

  function capturePhoto() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      void handleNewFile(file);
    }, 'image/jpeg', 0.9);
    stopCamera();
  }

  useEffect(() => {
    if (cameraActive) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraActive, cameraFacing]);

  /**
   * THIS BLOCK'S OWN, with no fall back to the asset's library description.
   *
   * An asset's description belongs to the FILE, and the same file is placed in other tasks and
   * other experiences — so inheriting it at render time substitutes a description written for a
   * context this block knows nothing about. The library modal already offers those previous
   * placements as an explicit choice when the asset is PICKED, naming the experience and task each
   * one came from, and the teacher's pick is copied into the field below. That is where a
   * description from elsewhere may enter: deliberately, visibly, and editable afterwards.
   *
   * It also makes the renderer and `imageIssues` agree again. They briefly did not: the check
   * asked for this block's own text while the renderer quietly supplied someone else's.
   */
  const effectiveAlt = data.altText ?? '';
  /* THIS BLOCK'S OWN, like the alt text above. A caption is visible content rather than an
     accessibility obligation, so an empty one raises no issue — but a caption written for the same
     file in another task is no more this question's caption than its description was. */
  const effectiveCaption = data.caption ?? '';
  const effectiveMotion = data.motionDescription ?? '';
  // Until the asset resolves nothing is known about whether it moves, so the motion-description
  // issue simply does not fire yet. The same shape as alt text's own `!assetId` guard: an issue
  // raised on a guess would appear and then vanish as the fetch lands.
  const isAnimatedAsset = !!cachedAsset?.animated;
  const issues = imageIssues(data, issueCtx);
  // Keyed by issue KEY, not by `issues.length`. With one possible issue those were the same thing;
  // with two, a missing motion description would have put the error ring on the alt-text box.
  const missingAltText = issues.some(i => i.key === 'alt-text-missing');
  const missingMotion = issues.some(i => i.key === 'motion-description-missing');

  if (isPreview) {
    // Nothing placed at all is the one case that stays empty: a student can never meet a block with
    // no image in it, the editor already calls that an error, and a "no image yet" note is authoring
    // scaffolding rather than anything a student should read.
    if (!data.assetId) return null;
    return cachedAsset ? (
      <figure className='w-full'>
        <AnimatedImage
          asset={cachedAsset}
          alt={effectiveAlt}
          motionDescription={effectiveMotion}
          autoplay={data.autoplay}
          className={previewImgClasses(data.aspectRatio, 'mx-auto block rounded-lg', PREVIEW_HEIGHT_CLASS, true)}
        />
        {effectiveCaption && (
          <PreviewHtml as='figcaption' html={effectiveCaption} className='ws-rich-text mt-2 text-xs text-ws-muted' />
        )}
      </figure>
    ) : (
      // Same silent disappearance Sound had, and the same fix — see `MediaPlaceholder`.
      //
      // `boxClass` is the image's own height, so the gap before an image arrives takes up
      // exactly the room the image will. It stood 64px tall against the image's 256, which
      // meant every gap collapsed the block by 192px and sprang back when it landed.
      <MediaPlaceholder
        boxClass={PREVIEW_HEIGHT_CLASS}
        message={t(assetFailed ? 'blocks.image.loadError' : 'blocks.image.loading')}
        failed={assetFailed}
        retryLabel={t('blocks.image.tryAgain')}
        onRetry={refreshCachedAsset}
      />
    );
  }

  if (!isEditing) {
    const imgAriaLabel = effectiveAlt ? t('blocks.image.editAriaWithAlt', { altText: effectiveAlt }) : cachedAsset?.filename ? t('blocks.image.editAriaWithFile', { fileName: cachedAsset.filename }) : t('blocks.image.editAriaNoImage');
    const contentProps = {
      tabIndex: 0 as const,
      role: 'button' as const,
      'aria-label': imgAriaLabel,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (isDeleteArmed) { disarmDelete(); return; }
          if (onSelect) onSelect(); else setIsEditing(true);
        }
      },
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        if (e.detail === 0) return;
        if (isDeleteArmed) { disarmDelete(); return; }
        if (onSelect) onSelect(); else setIsEditing(true);
      },
    };
    const contentChildren = (
      <>
        {/* The same gate Text uses: on selection because the card is then a stand-in for a
            student, and on a CLOSED card only while this block is actually leading a group —
            `leadsGroup` requires a real bank, so a one-variant block never grows a bar. */}
        {(isSelected || leadsGroup) && bank.length > 1 && (
          <DisplayScenarioBar
            bank={bank}
            activeId={shownVersionId}
            onSelect={setTestVersion}
            /* NO DICE. A reroll rolls VARIABLES and never the variant — the pager already
               steps scenarios, deliberately — and this block holds none. Only full Preview
               draws a scenario at random. See `DisplayScenarioBar.onReroll`. */
          />
        )}
        {data.assetId && cachedAsset ? (
          <AnimatedImage
            asset={cachedAsset}
            alt={effectiveAlt}
            motionDescription={effectiveMotion}
            autoplay={data.autoplay}
            className={previewImgClasses(data.aspectRatio, 'mx-auto block rounded-lg')}
          />
        ) : data.assetId ? (
          /*
           * The SAME reserved box Preview uses, for the same reason — this card was still the
           * one-line version.
           *
           * A line of italic text stands about 20px where the image will stand 256, so every
           * image block on the canvas collapsed to a sliver and sprang open when its asset
           * arrived. One block doing that is a flicker; a task of them shoves everything below
           * up and back down, which is what stepping between tasks looked like once the editor
           * stopped blanking in between and the gaps became visible at all.
           *
           * Not always "Loading", as before: a failed fetch left this saying so indefinitely,
           * which is the same silence as Preview's missing block wearing a more reassuring face.
           * `MediaPlaceholder` carries the retry, which the bare paragraph never had.
           */
          <MediaPlaceholder
            boxClass={PREVIEW_HEIGHT_CLASS}
            message={t(assetFailed ? 'blocks.image.loadError' : 'blocks.image.loading')}
            failed={assetFailed}
            retryLabel={t('blocks.image.tryAgain')}
            onRetry={refreshCachedAsset}
          />
        ) : (
          <p className='text-sm italic text-ws-muted'>{t('blocks.image.empty')}</p>
        )}
        {effectiveCaption && <PreviewHtml html={effectiveCaption} className='ws-rich-text mt-2 text-xs text-ws-muted' />}
      </>
    );

    if (isMain) {
      return (
        <BlockCard
          block={block}
          cardRef={blockRef}
          icon={<ImageIcon className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
          isDeleteArmed={isDeleteArmed}
          isSelected={isSelected}
          isDragOver={isDragOver}
          onDuplicate={triggerDuplicate}
          onDelete={triggerDelete}
          onDisarm={disarmDelete}
        >
          <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
            <div ref={collapsedContentRef} {...contentProps} className='relative flex-1 cursor-pointer p-4 pb-8 focus-visible:[outline:2px_auto]'>
              {contentChildren}
            </div>
            {issues[0] && <IssueBar onActivate={() => { if (onSelect) onSelect(); else setIsEditing(true); }} issue={issues[0]} className='border-t border-ws-border' />}
          </div>
        </BlockCard>
      );
    }

    return (
      <BlockPanelCard
        blockId={block.id}
        cardRef={blockRef}
        icon={<ImageIcon className='h-6 w-6' strokeWidth={2} />}
        title={t('blocks.image.title')}
        isDeleteArmed={isDeleteArmed}
        isSelected={isSelected}
        isDragOver={isDragOver}
        onDuplicate={triggerDuplicate}
        onDelete={triggerDelete}
        onDisarm={disarmDelete}
      >
        <div ref={collapsedContentRef} {...contentProps} className={`relative p-4 pb-8 cursor-pointer focus-visible:[outline:2px_auto] ${missingAltText ? '' : 'rounded-b-xl'}`}>
          {contentChildren}
        </div>
        {issues[0] && <IssueBar onActivate={() => { if (onSelect) onSelect(); else setIsEditing(true); }} issue={issues[0]} className='rounded-b-xl' />}
      </BlockPanelCard>
    );
  }

  const currentAspectLabelKey = ASPECT_RATIOS.find((r) => r.value === data.aspectRatio)?.labelKey ?? 'blocks.image.aspectOriginal';
  const previewHeight = condensed ? CONDENSED_PREVIEW_HEIGHT_CLASS : PREVIEW_HEIGHT_CLASS;
  /* Two rows rather than three, and a caption box a line and a half rather than three. A
     condensed row is one of five in a dialog, and all three boxes grow with what is typed into
     them — the taller default is reserving room nobody has asked for yet. */
  const proseRows = condensed ? 2 : 3;


  const editView = (
    <div className={usePortal
      ? 'fixed inset-x-0 top-0 z-[100] flex flex-col bg-ws-surface h-[100dvh]'
      : condensed
        ? 'flex flex-col'
        : 'flex flex-col rounded-xl border border-ws-border bg-ws-surface'
    }>
      {/* Header — the block's name and the way out of the editor, neither of which a condensed
          host has any use for: it frames the block itself and its rows carry no names. */}
      {!condensed && (
      <div className={`flex items-center gap-2 border-b border-ws-border bg-ws-hover px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link${!usePortal ? ' rounded-t-xl' : ''}`}>
        <ImageIcon className='h-6 w-6' strokeWidth={2} />
        <BlockNameField
          blockName={block.name ?? ''}
          titleLabel={t('blocks.image.title')}
          onBlockNameChange={onRename}
          isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks, block.id)}
        />
        <button
          onClick={handleDone}
          aria-label={t('blocks.image.closeEditor')}
          className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10'
        >
          <X className='h-5 w-5' />
        </button>
      </div>
      )}
      {phoneTest?.tabs}
      {phoneTest?.pane}
      <div className={`flex flex-col ${condensed ? 'gap-4 p-3' : 'gap-6 p-6'}${usePortal ? ' flex-1 overflow-y-auto' : ''}${phoneTest?.pane ? ' hidden' : ''}`}>
        {/* THE BANK, first in the body — a variant decides which image this is, so it has to be
            settled before anything below it is read. `divider` because, unlike Text, there is no
            styling bar above drawing one.

            Absent when condensed: the only condensed host is the exit editor, and an exit is read
            after the flow has already routed — there is no scenario stepping through it to pick a
            variant, so a bank there is a control with nothing to switch. */}
        {!condensed && (
        <DisplayBankRow
          bank={bank}
          editorVersionId={editorVersionId}
          setEditorVersion={setEditorVersion}
          addVariant={addVariant}
          removeVariant={removeVariant}
          renameVariant={renameVariant}
          removeWarning={removeWarning}
        />
        )}

      {data.assetId ? (
        /*
         * TWO COLUMNS WHEN CONDENSED: the picture and how it is framed on the left, what it
         * SAYS on the right.
         *
         * Measured before touching it, one placed image row came to 592px inside a dialog whose
         * whole scrolling body is 400 — so a single image did not fit, and the cap is five. The
         * dialog is 860px wide and this was using under half of it, because the editor's own
         * layout is a column built for a panel standing beside the canvas.
         *
         * The split is the one the content already has rather than an arbitrary halving:
         * everything about the ASSET — the preview, its name, the crop, whether it plays — on
         * one side, and the three pieces of writing on the other. It stacks again below `sm`,
         * where there is no width to divide.
         *
         * `contents` everywhere when NOT condensed, so the canvas editor keeps the single
         * column it has: these three wrappers then contribute no boxes at all and every child
         * lands in the body's own flex column exactly as before.
         */
        <div className={condensed
          ? 'grid gap-x-4 gap-y-3 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]'
          : 'contents'}>
        <div className={condensed ? 'flex min-w-0 flex-col gap-3' : 'contents'}>
            {/* Placed image — fixed height regardless of aspect ratio; switching ratios only
                changes the crop/width, never the height, so it never reflows the fields below.

                THE PICTURE IS NOT A BUTTON. It was: the whole thumbnail opened the Library, which
                nothing announced and which is a surprising thing for an image to do — clicking a
                picture to REPLACE it is not a guess anyone makes, and it is a destructive-feeling
                action to reach by accident. Swap and clear are their own controls now, exactly as
                Sound has had them.

                Sound's own note said why it diverged: a real player "can't also double as a single
                'click to swap' target the way Image's <img> does". That stopped being the
                difference the moment an animated image grew a play/pause control — the content here
                is interactive too now, and a swap target wrapped around a button is the nested-
                interactive problem Sound avoided. The two blocks agree again.

                And because the preview is no longer inside a button, it can ANIMATE: a teacher
                setting up an animation can see the thing they are describing, and pause it. */}
            <div className='relative'>
              <div className={`flex w-full items-center justify-center overflow-hidden rounded-lg bg-ws-btn ${previewHeight}`}>
                {cachedAsset ? (
                  <AnimatedImage
                    asset={cachedAsset}
                    alt=''
                    autoplay={data.autoplay}
                    className={previewImgClasses(data.aspectRatio, '', previewHeight)}
                  />
                ) : (
                  <p className='text-sm italic text-ws-muted'>{t('blocks.image.loading')}</p>
                )}
              </div>
              <MediaOverlayControls
                onSwap={() => setShowLibraryModal(true)}
                swapLabel={t('blocks.image.openLibraryToSwap')}
                onClear={handleClear}
                clearLabel={t('blocks.image.clear')}
              />
            </div>

            {/* Name — only offered right after a fresh upload/capture; hidden for a reused Library
                asset or once the editor has been closed and reopened. */}
            {justUploaded && (
              <div className='flex flex-col'>
                <label className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.image.assetName')}</label>
                <TextField
                  variant='prose'
                  value={renameValue}
                  onChange={setRenameValue}
                  onBlur={saveRename}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  ariaLabel={t('blocks.image.assetName')} />
              </div>
            )}

            {/* Aspect ratio (dropdown) */}
            <div>
              <p className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.image.aspectRatio')}</p>
              <div ref={aspectRef} className='relative'>
                <button
                  type='button'
                  data-field-id='aspectRatio'
                  onClick={() => setAspectOpen((v) => !v)}
                  className='flex w-full items-center justify-between rounded-md border border-ws-border bg-transparent px-3 py-2 text-sm text-ws-text hover:bg-ws-hover'
                >
                  <span className='flex items-center gap-2'>
                    <AspectSwatch value={data.aspectRatio} />
                    {t(currentAspectLabelKey)}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-ws-muted transition-transform ${aspectOpen ? 'rotate-180' : ''}`} />
                </button>
                {aspectOpen && (
                  <div className='absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-md border border-ws-border bg-ws-elevated shadow-lg'>
                    {ASPECT_RATIOS.map((r) => (
                      <button
                        key={r.value}
                        type='button'
                        onClick={() => { set({ aspectRatio: r.value }); setAspectOpen(false); }}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-ws-text hover:bg-ws-hover ${
                          data.aspectRatio === r.value ? 'bg-ws-hover' : ''
                        }`}
                      >
                        <AspectSwatch value={r.value} />
                        {t(r.labelKey)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Plays automatically — only for an asset that moves. Display blocks have no options
                drawer, so a presentation setting lives here beside the aspect ratio, which is the
                other one.

                It governs everyone who has NOT asked their device for less motion:
                `prefers-reduced-motion` always wins, and `AnimatedImage` enforces that rather than
                trusting this value. A control that could defeat a viewer's own accessibility
                setting would be a bug wearing a preference's clothes. */}
            {isAnimatedAsset && (
              <CheckboxRow
                fieldId='autoplay'
                checked={data.autoplay !== false}
                onToggle={() => set({ autoplay: !(data.autoplay !== false) })}
                label={t('blocks.image.autoplay')}
              />
            )}

        </div>
        <div className={condensed ? 'flex min-w-0 flex-col gap-3' : 'contents'}>
            {/* Alt text */}
            <div className='flex flex-col'>
              <label className='mb-1.5 flex items-center gap-1 text-sm font-medium text-ws-text'>
                {t('blocks.image.altText')}
                <span className='text-ws-muted font-normal'>{t('blocks.image.altRequired')}</span>
              </label>
              {/* Stays PLAIN, unlike the caption below it: alt text is delivered through the `alt`
                  attribute, which cannot carry markup. Rich content here is not unnecessary, it is
                  unrepresentable. */}
              <TextField
                variant='prose'
                rows={proseRows}
                fieldId={ALT_TEXT_FIELD}
                value={effectiveAlt}
                onChange={v => set({ altText: v })}
                placeholder={t('blocks.image.altPlaceholder')}
                hasError={missingAltText}
                describedBy={missingAltText ? missingAltTextWarningId : undefined}
                ariaLabel={t('blocks.image.altText')} />
              {/* See sound-block: hidden from view because the card's issue bar is where this is
                  shown, but kept in the accessibility tree so the field explains itself. */}
              {missingAltText && (
                <span id={missingAltTextWarningId} className='sr-only'>{t('blocks.image.missingAltText')}</span>
              )}
            </div>

            {/* Motion description — only for an asset that actually moves. */}
            {isAnimatedAsset && (
              <div className='flex flex-col'>
                <label className='mb-1.5 flex items-center gap-1 text-sm font-medium text-ws-text'>
                  {t('blocks.image.motionDescription')}
                  <span className='text-ws-muted font-normal'>{t('blocks.image.altRequired')}</span>
                </label>
                {/* Plain, like alt text and for a stronger reason: this is read aloud in place of
                    watching something, and formatting is not part of what changed. The LABEL is what
                    does the work here -- "Describe what changes" asks for an observation, where
                    "Description" invites a summary of the picture that the alt text already gave. */}
                <TextField
                  variant='prose'
                  rows={proseRows}
                  fieldId={MOTION_DESCRIPTION_FIELD}
                  value={effectiveMotion}
                  onChange={v => set({ motionDescription: v })}
                  placeholder={t('blocks.image.motionPlaceholder')}
                  hasError={missingMotion}
                  describedBy={missingMotion ? missingMotionWarningId : undefined}
                  ariaLabel={t('blocks.image.motionDescription')} />
                {missingMotion && (
                  <span id={missingMotionWarningId} className='sr-only'>{t('blocks.image.missingMotionDescription')}</span>
                )}
              </div>
            )}

            {/* Caption */}
            <div className='flex flex-col'>
              <label className='mb-1.5 flex items-center gap-1 text-sm font-medium text-ws-text'>
                {t('blocks.image.caption')}
                <span className='text-ws-muted font-normal'>{t('blocks.image.captionOptional')}</span>
              </label>
              {/* The one field of the three that earns rich text. Alt text CANNOT be rich (it is
                  delivered through the `alt` attribute), and a transcript is a record of speech with
                  no formatting to preserve — but a caption is display prose under a photograph,
                  where an italicised species name or a superscript unit is ordinary. Not the
                  superscript case Unicode already covers: this is about emphasis and structure. */}
              <RichTextEditor
                fieldId='caption'
                content={effectiveCaption}
                onChange={(html: string) => set({ caption: html })}
                placeholder={t('blocks.image.captionPlaceholder')}
                editorClassName={condensed ? 'min-h-10' : 'min-h-[4.5rem]'}
                hideToolbar={!!sharedToolbar}
                onEditorReady={(e) => { captionEditorRef.current = e; }}
                onEditorFocus={() => {
                  const e = captionEditorRef.current;
                  if (sharedToolbar && e) {
                    sharedToolbar.activeEditorRef.current = e;
                    sharedToolbar.setActiveEditor(e);
                  }
                }} />
            </div>
        </div>
        </div>
      ) : cameraActive ? (
        <div className='flex flex-col gap-3'>
          {cameraError ? (
            <div className='flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-ws-border py-10 px-4 text-center'>
              <Camera className='h-7 w-7 text-ws-muted' />
              <p className='text-sm text-ws-text'>{t('blocks.image.cameraUnavailable')}</p>
              <p className='text-xs text-ws-muted'>{cameraError}</p>
              <button
                onClick={startCamera}
                className='mt-2 rounded-md border border-ws-border px-3 py-1.5 text-xs font-medium text-ws-text hover:bg-ws-hover'
              >
                {t('blocks.image.tryAgain')}
              </button>
            </div>
          ) : (
            <div className='relative'>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className='w-full rounded-lg bg-ws-btn aspect-video object-cover'
              />
              <button
                type='button'
                onClick={flipCamera}
                aria-label={t('blocks.image.flipCamera')}
                title={t('blocks.image.flipCamera')}
                className='absolute top-2 right-2 flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80'
              >
                <SwitchCamera className='h-5 w-5' />
              </button>
            </div>
          )}
          <div className='flex items-center justify-center gap-4'>
            <button
              type='button'
              onClick={() => setCameraActive(false)}
              className='rounded-md border border-ws-border px-3 py-2 text-sm font-medium text-ws-text hover:bg-ws-hover'
            >
              {t('blocks.image.cancelCamera')}
            </button>
            {!cameraError && (
              <button
                type='button'
                onClick={capturePhoto}
                disabled={uploading}
                aria-label={uploading ? t('blocks.image.uploading') : t('blocks.image.capture')}
                title={t('blocks.image.capture')}
                className='flex h-16 w-16 items-center justify-center rounded-full bg-ws-primary text-ws-btn-on-primary shadow-lg hover:opacity-90 disabled:opacity-50'
              >
                <Camera className='h-7 w-7' />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          /*
           * CONDENSED LOSES THE DROP ZONE'S OWN BOX.
           *
           * The empty state is a dashed panel because on the canvas it has to read as a place
           * to drop a file into. Inside a host that has already drawn a frame round this row it
           * is the third border in a row — row frame, dashed panel, then each tile's own — and
           * the stack of them was most of why an empty media row ran to ~190px. The drop
           * handlers stay: dropping still works, it just no longer advertises itself with an
           * outline the host has already drawn.
           */
          className={condensed
            ? `flex flex-wrap items-center gap-2 rounded-lg transition-colors ${isDragging ? 'bg-ws-primary/5' : ''}`
            : `flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed py-8 px-4 transition-colors ${
              isDragging ? 'border-ws-primary bg-ws-primary/5' : 'border-ws-border'}`}
        >
          <div className='flex gap-3'>
            {/* Tiles, not buttons in a row: choosing a source IS the content of this empty state,
                so they keep the stacked shape. Shared with the block rail, which had grown the
                same markup independently — see `ChoiceTile`. */}
            <ChoiceTile
              icon={<Images />}
              label={t('blocks.image.tabs.library')}
              onClick={() => setShowLibraryModal(true)}
            />
            <ChoiceTile
              icon={<Upload />}
              label={uploading ? t('blocks.image.uploading') : t('blocks.image.tabs.upload')}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            />
            <ChoiceTile
              icon={<Camera />}
              label={t('blocks.image.tabs.camera')}
              onClick={() => setCameraActive(true)}
            />
          </div>
          {/* The format list is reference, not instruction, and a condensed row has no space to
              spend on it — the picker rejects what it cannot take and says so then. */}
          {!condensed && <span className='text-xs text-ws-muted'>{t('blocks.image.supported')}</span>}
        </div>
      )}

      <input
        ref={fileInputRef}
        type='file'
        accept={IMAGE_ACCEPT}
        className='sr-only'
        /* OUT OF THE TAB ORDER. `sr-only` hides it from sight but NOT from the keyboard, so
           this sat in the tab order as a silent extra stop beside the visible control that
           opens it: a file input with no label, no `aria-label` and nothing to announce. It
           is driven by a ref from that control and is not the accessible affordance — the
           button is. (`hidden`, which the avatar and CSV pickers use, has never had this
           problem: `display:none` is not focusable.) */
        tabIndex={-1}
        onChange={handleFileChange}
      />
      </div>
      {toastMsg && <Toast message={toastMsg} type='error' onDismiss={() => setToastMsg('')} />}
      {showLibraryModal && (
        <LibraryModal
          assetKind='image'
          experienceId={experienceId}
          currentAssetId={data.assetId}
          currentTaskId={taskId}
          currentTaskBlocks={blocks}
          onSelect={handleLibrarySelect}
          onClose={() => setShowLibraryModal(false)}
        />
      )}
    </div>
  );

  return usePortal ? createPortal(editView, document.body) : editView;
}
